import moment from "moment";
import {
  createBaseRoomEntry,
  transformRoomTypesToFloorsCeilings,
} from "./seasonAndEvents.helper";

export interface CopyableSeason {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  weekdayPriceFloor: number;
  weekendPriceFloor: number;
  weekdayPriceCeiling: number;
  weekendPriceCeiling: number;
  roomTypeFloorsCeilings?: {
    [roomTypeId: string]: {
      weekdayFloor: number | null;
      weekdayCeiling: number | null;
      weekendFloor: number | null;
      weekendCeiling: number | null;
    };
  };
  roomTypes?: {
    id: string;
    weekdayRateFloor: number | null;
    weekdayRateCeiling: number | null;
    weekendRateFloor: number | null;
    weekendRateCeiling: number | null;
  }[];
}

export interface CopyPlan {
  source: CopyableSeason;
  startDate: string;
  endDate: string;
  conflicts: CopyableSeason[];
}

// "YYYY-MM-DD" + 1 year. moment clamps Feb 29 -> Feb 28 when the
// target year is not a leap year, which is the behavior we want.
export const shiftDateOneYear = (dateStr: string): string =>
  moment(dateStr, "YYYY-MM-DD").add(1, "year").format("YYYY-MM-DD");

// Half-open-agnostic overlap test on YYYY-MM-DD strings (lexicographic ==
// chronological). Mirrors the backend isSeasonConflict check.
export const seasonsOverlap = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean => aStart <= bEnd && aEnd >= bStart;

export const buildCopyPlans = (
  sourceSeasons: CopyableSeason[],
  allSeasons: CopyableSeason[],
): CopyPlan[] => {
  const sourceIds = new Set(sourceSeasons.map((s) => s.id));
  return sourceSeasons.map((source) => {
    const startDate = shiftDateOneYear(source.startDate);
    const endDate = shiftDateOneYear(source.endDate);
    const conflicts = allSeasons.filter(
      (other) =>
        !sourceIds.has(other.id) &&
        seasonsOverlap(startDate, endDate, other.startDate, other.endDate),
    );
    return { source, startDate, endDate, conflicts };
  });
};

type SeasonOverrideMap = NonNullable<CopyableSeason["roomTypeFloorsCeilings"]>;

// On a fresh load, per-room overrides live in season.roomTypes (not in
// roomTypeFloorsCeilings, which is only populated after an in-session edit).
// Reproduce the exact map the manual-edit save would send so a copied season
// faithfully carries the customer's per-room overrides.
export const resolveSeasonOverrides = (
  season: CopyableSeason,
  baseRoomTypeId?: string,
): SeasonOverrideMap => {
  const existing = season.roomTypeFloorsCeilings;
  if (existing && Object.keys(existing).length > 0) {
    return existing;
  }
  const roomTypes = season.roomTypes ?? [];
  if (roomTypes.length === 0) {
    return {};
  }
  const baseEntry = baseRoomTypeId
    ? createBaseRoomEntry(season, baseRoomTypeId)
    : {};
  return {
    ...baseEntry,
    ...transformRoomTypesToFloorsCeilings(roomTypes),
  };
};

export interface AddSeasonData {
  userId: string;
  name: string;
  startDate: string;
  endDate: string;
  weekdayPriceFloor: number;
  weekendPriceFloor: number;
  weekdayPriceCeiling: number;
  weekendPriceCeiling: number;
}

export interface UpdateSeasonData {
  userId: string;
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  roomTypeFloorsCeilings: NonNullable<CopyableSeason["roomTypeFloorsCeilings"]>;
}

export interface SeasonCopyApi {
  addSeason: (
    userId: string,
    data: AddSeasonData,
  ) => Promise<{ data: { season: { id: string } } }>;
  updateSeason: (userId: string, data: UpdateSeasonData) => Promise<unknown>;
  deleteSeason: (userId: string, seasonId: string) => Promise<unknown>;
}

export const runSeasonCopy = async ({
  userId,
  plans,
  overwrite,
  api,
}: {
  userId: string;
  plans: CopyPlan[];
  overwrite: boolean;
  api: SeasonCopyApi;
}): Promise<{
  copied: number;
  overwritten: number;
  failures: string[];
  overridesSkipped: string[];
}> => {
  // Delete conflicting target seasons first (addSeason rejects overlaps).
  // A failed delete leaves the slot occupied, so the dependent addSeason below
  // surfaces as a per-season failure rather than aborting the whole batch.
  let overwritten = 0;
  const failures: string[] = [];
  // Seasons created successfully but whose per-room overrides could not be
  // applied (the create returned no id) — surfaced so the caller can warn.
  const overridesSkipped: string[] = [];
  if (overwrite) {
    const ids = Array.from(
      new Set<string>(plans.flatMap((p) => p.conflicts.map((c) => c.id))),
    );
    for (const id of ids) {
      try {
        await api.deleteSeason(userId, id);
        overwritten += 1;
      } catch {
        // Keep going; the dependent addSeason will be reported as a failure.
      }
    }
  }

  // Isolate each season: one failed create/update must not strand the rest of
  // the batch. Failures are collected and reported so the caller can resync and
  // tell the user exactly which seasons need re-copying.
  let copied = 0;
  for (const plan of plans) {
    const { source, startDate, endDate } = plan;
    try {
      const res = await api.addSeason(userId, {
        userId,
        name: source.name,
        startDate,
        endDate,
        weekdayPriceFloor: source.weekdayPriceFloor,
        weekendPriceFloor: source.weekendPriceFloor,
        weekdayPriceCeiling: source.weekdayPriceCeiling,
        weekendPriceCeiling: source.weekendPriceCeiling,
      });

      const newId = res?.data?.season?.id;
      const overrides = source.roomTypeFloorsCeilings;
      const hasOverrides = overrides && Object.keys(overrides).length > 0;
      if (newId && hasOverrides) {
        await api.updateSeason(userId, {
          userId,
          id: newId,
          name: source.name,
          startDate,
          endDate,
          roomTypeFloorsCeilings: overrides,
        });
      } else if (!newId && hasOverrides) {
        console.warn(
          `Season "${source.name}" was created without an id; per-room overrides were not copied.`,
        );
        overridesSkipped.push(source.name);
      }
      copied += 1;
    } catch {
      failures.push(source.name);
    }
  }

  return { copied, overwritten, failures, overridesSkipped };
};
